<style scoped>
.modal-bubbleBox {
    max-height: calc(100vh - 8rem);
    overflow: auto;
}
</style>

<template>
    <modal type="custom" class="modal-2xl">
        <div>
            <div
                class="text-darkGrayishBlue text-xl sm:text-2xl text-center mb-2"
            >
                {{ $t('product.feedback.account.info.greet') }}
            </div>
            <div class="border border-darkGrayishBlue rounded">
                <div class="pt-4 px-4 sm:px-8">
                    <formFieldRowByRule
                        name="title"
                        class="field-title"
                        :label="$t('account.title')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template #default="{ hasError }">
                            <select
                                class="form-select"
                                v-model.trim="form.title"
                                :class="{ 'border-red': hasError }"
                            >
                                <option disabled :value="null"> </option>
                                <option
                                    v-for="c in genderCodeList"
                                    :key="c"
                                    :value="c"
                                >
                                    {{ $t('account.title.' + c) }}
                                </option>
                            </select>
                        </template>
                    </formFieldRowByRule>
                    <formFieldRowByRule
                        :name="['surname', 'givenName']"
                        class="field-surname field-givenName"
                        :label="$t('account.name')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template #default="{hasError}">
                            <div class="flex flex-col sm:flex-row items-start">
                                <input
                                    class="form-input w-full mb-2 sm:w-32 sm:mr-2 sm:mb-0"
                                    :class="{ 'border-red': hasError }"
                                    type="text"
                                    v-model.trim="form.surname"
                                    :placeholder="$t('account.surname')"
                                />
                                <input
                                    class="form-input w-full sm:flex-1"
                                    :class="{ 'border-red': hasError }"
                                    type="text"
                                    v-model.trim="form.givenName"
                                    :placeholder="$t('account.given.name')"
                                />
                            </div>
                        </template>
                    </formFieldRowByRule>
                    <formFieldRowByRule
                        name="email"
                        class="field-email"
                        :label="$t('account.email')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template #default="{ hasError }">
                            <input
                                class="form-input w-full"
                                :class="{ 'border-red': hasError }"
                                :value="form.email"
                                :readonly="true"
                            />
                        </template>
                    </formFieldRowByRule>
                    <formFieldRowByRule
                        name="birthday"
                        class="field-birthday"
                        :label="$t('account.birthday')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template>
                            <monthPicker
                                v-if="allowEditBirthday"
                                :value="form.birthday"
                                @input="
                                    v => {
                                        form.birthday = v
                                            ? v.valueFormatted
                                            : undefined;
                                    }
                                "
                                :minYear="new Date().getFullYear() - 100"
                                :maxYear="new Date().getFullYear() - 18"
                                :format="'yyyy-MM-dd'"
                            />
                            <input
                                v-else
                                class="form-input w-full"
                                :value="formatDate(form.birthday, 'yearMonth')"
                                readonly
                            />
                            <div class="text-blue">
                                {{ $t('account.birthday.remind.message') }}
                            </div>
                            <div
                                v-if="showBirthdayTcoinGiftMessage"
                                class="text-blue"
                            >
                                {{ $t('account.birthday.gift.message') }}
                            </div>
                        </template>
                    </formFieldRowByRule>
                    <formFieldRowByRule
                        name="occupation"
                        class="field-occupation"
                        :label="$t('account.occupation')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template #default="{ hasError }">
                            <select
                                class="form-select"
                                v-model.trim="form.occupation"
                                :class="{ 'border-red': hasError }"
                            >
                                <option disabled :value="null"> </option>
                                <option
                                    v-for="o in occupationList"
                                    :key="o.id"
                                    :value="o.id"
                                >
                                    {{ $t(o.slctDesp) }}
                                </option>
                            </select>
                        </template>
                    </formFieldRowByRule>
                    <formFieldRowByRule
                        name="jobLevel"
                        class="field-jobLevel"
                        :label="$t('account.job.level')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template #default="{hasError}">
                            <select
                                class="form-select"
                                v-model.trim="form.jobLevel"
                                :class="{ 'border-red': hasError }"
                            >
                                <option disabled :value="null"></option>
                                <option
                                    v-for="j in jobLevelList"
                                    :key="j.id"
                                    :value="j.id"
                                >
                                    {{ $t(j.slctDesp) }}
                                </option>
                            </select>
                        </template>
                    </formFieldRowByRule>
                    <formFieldRowByRule
                        name="monthlyIncome"
                        class="field-monthlyIncome"
                        :label="$t('account.monthly.income')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template #default="{hasError}">
                            <select
                                class="form-select"
                                v-model.trim="form.monthlyIncome"
                                :class="{ 'border-red': hasError }"
                            >
                                <option disabled :value="null"></option>
                                <option
                                    v-for="j in monthlyIncomeList"
                                    :key="j.id"
                                    :value="j.id"
                                >
                                    {{ $t(j.slctDesp) }}
                                </option>
                            </select>
                        </template>
                    </formFieldRowByRule>

                    <formFieldRowByRule
                        name="phone"
                        class="field-phone"
                        :label="$t('account.phone')"
                        :errors="errors"
                        :rules="validationRules"
                    >
                        <template #default="{ hasError }">
                            <input
                                class="form-input w-full"
                                :class="{ 'border-red': hasError }"
                                type="tel"
                                v-model.trim="form.phone"
                            />
                        </template>
                    </formFieldRowByRule>
                </div>
            </div>
            <errorBox :message="errors.server" />
            <div class="flex justify-between mt-4 sm:pb-4">
                <button
                    class="btn justify-center mr-2 w-1/2"
                    @click="$emit('hide')"
                >
                    {{ $t('product.feedback.account.info.back.to.home') }}
                </button>
                <loadingButton
                    class="bg-primary ml-2 w-1/2 justify-center"
                    :status="saveStatus"
                    @click="save"
                    @successShown="$emit('success')"
                >
                    {{ $t('common.confirm') }}
                </loadingButton>
            </div>
        </div>
    </modal>
</template>

<script>
import modal from '@/components/modal.vue';
import loadingButton from '@/components/loadingButton.vue';
import formFieldRowByRule from '@/components/formFieldRowByRule.vue';
import errorBox from '@/components/errorBox.vue';
import { validate } from '@/utils/validationUtil';
import { instance } from '@/utils/ajax';
import monthPicker from '@/components/monthPicker.vue';
import formatDate from '../components/formatDateFilter';

const validationRulesGeneral = {
    title: { required: true, show: true },
    surname: { required: true, show: true },
    givenName: { required: true, show: true },
    email: { required: true, show: true },
    occupation: { required: !!false, show: !!false },
    jobLevel: { required: true, show: true },
    monthlyIncome: { required: true, show: true },
    birthday: { required: true, show: true },
    phone: { required: true, show: true }
};

const validationRulesFriends = {
    ...validationRulesGeneral,
    occupation: { required: !!false, show: true },
    jobLevel: { required: !!false, show: true },
    monthlyIncome: { required: !!false, show: true },
    birthday: { required: !!false, show: true }
};

const validationRulesPublic = {
    ...validationRulesGeneral,
    occupation: { required: !!false, show: true },
    jobLevel: { required: !!false, show: true },
    monthlyIncome: { required: !!false, show: true },
    birthday: { required: !!false, show: true }
};

async function save(form) {
    try {
        let result = await instance.post(
            '/api/productFeedback/saveAccountInfo',
            form
        );
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error saveAccountInfo');
        return { success: false, all: 'error.connect' };
    }
}

export default {
    components: {
        modal,
        loadingButton,
        formFieldRowByRule,
        errorBox,
        monthPicker
    },
    props: {
        prodFdbkEmailTknNum: { type: String },
        custTypeCode: { type: String, required: true },
        productFeedbackAccountInfoForm: { type: Object, required: true },
        occupationList: { type: Array, required: true },
        jobLevelList: { type: Array, required: true },
        monthlyIncomeList: { type: Array, required: true },
        genderCodeList: { type: Array, required: true },
        showBirthdayTcoinGiftMessage: { type: Boolean, default: () => false }
    },
    data() {
        const form = this.productFeedbackAccountInfoForm;
        return {
            errors: {},
            saveStatus: undefined,
            form: {
                title: form.title,
                surname: form.surname,
                givenName: form.givenName,
                birthday: form.birthday,
                jobLevel: form.jobLevel,
                monthlyIncome: form.monthlyIncome,
                email: form.email,
                phone: form.phone,
                occupation: form.occupation
            },
            allowEditBirthday: !this.productFeedbackAccountInfoForm.birthday
        };
    },
    computed: {
        validationRules() {
            if (this.custTypeCode === 'FRND') {
                return validationRulesFriends;
            }
            if (this.custTypeCode === 'PUB') {
                return validationRulesPublic;
            }
            return validationRulesGeneral;
        }
    },
    methods: {
        formatDate: formatDate,
        async save() {
            this.errors = {};
            this.form.phone = this.form.phone?.replace(/\s/g, '');
            const v = validate(this.errors, this.form, this.validationRules);
            if (v.hasError) {
                this.errors = v.errors;
                this.scrollToError();
                return;
            }
            this.saveStatus = 'loading';
            const result = await save({
                ...this.form,
                prodFdbkEmailTknNum: this.prodFdbkEmailTknNum
            });
            if (result.success) {
                this.saveStatus = 'success';
                //this.$store.commit('setUser', result.data.user);
            } else {
                this.saveStatus = undefined;
                this.errors = result.data.errors;
            }
        },
        scrollToError() {
            const field = Object.keys(this.errors)[0];
            if (field) {
                const target = this.$el.querySelector('.field-' + field);
                target.scrollIntoView();
            }
        }
    }
};
</script>
